import { useEffect, useState } from "react";
import useGet from "../../../global/hooks/useGet";
import useDelete from "../../../global/hooks/useDelete";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { DeviceProductionItemsInfoColumns } from "../Page/DeviceProductionItemsInfoColumns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import DeviceIcon from "../../../assets/images/vectors/SideBarIcons/DeviceIcon";
import DeviceProductionModal from "./DeviceProductionModal";
import { getDeviceProductionListPath } from "../utils/listSearchStorage";

export const DeviceProductionItemForm = () => {
  const { id, itemid } = useParams();
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState({
    addByCountModal: false,
    addByListModal: false,
  });
  const [selectedDeviceKeys, setSelectedDeviceKeys] = useState([]);
  const [showDeleteDeviceModal, setShowDeleteDeviceModal] = useState(false);

  const devicesUrl =
    id && itemid
      ? `${process.env.REACT_APP_API_URL}/api/${id}/device-production-items/${itemid}/`
      : null;
  const { data: devicesData, loading: devicesLoading, refetch: devicesRefetch } =
    useGet(devicesUrl);

  const productionDetailUrl = id
    ? `${process.env.REACT_APP_API_URL}/api/device-production/${id}/`
    : null;
  const { data: productionDetailData } = useGet(productionDetailUrl);

  const deleteDeviceUrl =
    selectedDeviceKeys.length > 1
      ? `${process.env.REACT_APP_API_URL}/api/${id}/device-production-items/${itemid}/bulk-delete-item-device/`
      : `${process.env.REACT_APP_API_URL}/api/${id}/device-production-items/${itemid}/bulk-delete-item-device/`;

  const {
    data: deletedDeviceData,
    error: deletedDeviceError,
    deleteData: deleteDevice,
    deleteMultiple: deleteDevices,
  } = useDelete(deleteDeviceUrl);

  const onDeleteDeviceConfirm = async () => {
    try {
      if (selectedDeviceKeys.length === 1) await deleteDevice();
      else await deleteDevices({ ids: selectedDeviceKeys });
    } catch {
      openErrorNotification("Something went wrong.");
    }
    setShowDeleteDeviceModal(false);
    setSelectedDeviceKeys([]);
  };

  useEffect(() => {
    if (deletedDeviceData && !deletedDeviceError) {
      openConfirmNotification("Success");
      devicesRefetch();
    } else if (deletedDeviceError) {
      openErrorNotification(
        deletedDeviceError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedDeviceData, deletedDeviceError, devicesRefetch]);

  const devicesTableData = (() => {
    const rows = devicesData?.data
      ? Array.isArray(devicesData.data)
        ? devicesData.data
        : devicesData.data?.devices || []
      : [];
    return rows
      .map((dev) => ({
        ...dev,
        key: dev.DeviceNo ?? dev.Mac,
      }))
      .sort((a, b) => (b.DeviceNo ?? 0) - (a.DeviceNo ?? 0));
  })();

  const breadcrumbItems = [
    { label: "Device Production", href: "/device-production" },
    {
      label: "Information",
      href: `/device-production/info/${id}`,
    },
    {
      label: "Item Information",
      href: `/device-production/info/${id}/item/${itemid}`,
    },
  ];

  const headerButtonsData = [
    {
      label: "List",
      icon: <UnorderedListOutlined />,
      onClick: () => navigate(getDeviceProductionListPath()),
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: () => navigate(`/device-production/info/${id}`),
    },
  ];

  const deviceButtonsData = [
    {
      label: "Add by Count",
      icon: <PlusOutlined />,
      onClick: () => {
        setOpenModal((prev) => ({ ...prev, addByCountModal: true }));
      },
    },
    {
      label: "Add by List",
      icon: <PlusOutlined />,
      onClick: () => {
        setOpenModal((prev) => ({ ...prev, addByListModal: true }));
      },
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowDeleteDeviceModal(true),
      disabled: selectedDeviceKeys.length === 0,
    },
  ];

  return (
    <>
      <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={headerButtonsData} />
        </CommonBreadcrumb>
      </div>

      <div className="flex justify-end mb-2">
        <CommonButton buttonsData={deviceButtonsData} />
      </div>

      <GlobalTable
        columns={DeviceProductionItemsInfoColumns()}
        dataSource={devicesTableData}
        rowSelection={{
          selectedRowKeys: selectedDeviceKeys,
          onChange: setSelectedDeviceKeys,
          fixed: true,
        }}
        onRow={() => ({})}
        loading={devicesLoading}
      />

      <ConfirmModal
        showModal={showDeleteDeviceModal}
        setShowModal={setShowDeleteDeviceModal}
        onDeleteConfirm={onDeleteDeviceConfirm}
        setSelectedRowKeys={setSelectedDeviceKeys}
      />

      <DeviceProductionModal
        isOpenModal={openModal.addByCountModal}
        setIsOpenModal={(value) =>
          setOpenModal((prev) => ({ ...prev, addByCountModal: value }))
        }
        productionRefetch={devicesRefetch}
        modalType="addByCount"
        productionId={id}
        itemId={itemid}
        LicenseNo={productionDetailData?.data?.LicenseNo}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <DeviceIcon className="text-[18px] text-black " />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add by Count
            </span>
          </div>
        }
      />
      <DeviceProductionModal
        isOpenModal={openModal.addByListModal}
        setIsOpenModal={(value) =>
          setOpenModal((prev) => ({ ...prev, addByListModal: value }))
        }
        productionRefetch={devicesRefetch}
        modalType="addByList"
        productionId={id}
        itemId={itemid}
        LicenseNo={productionDetailData?.data?.LicenseNo}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <DeviceIcon className="text-[18px] text-black " />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add by List
            </span>
          </div>
        }
      />
    </>
  );
};
